import { LineChart, AxisValueFormatterContext } from "@mui/x-charts";
import { DBAccount, DBPosition } from "../../../../api/types";
import { useCreateGraphFromPositions } from "./graph";
import { useRef } from "react";
import { getPositionPnLDollar } from "../../../../utils/utils";

type ContextType = {
  beginEquity: number | undefined;
  currentProfit: number;
};

const START_PROFIT = 0;

export default function AccountProfitGraph({
  account,
  positions,
  rangeStartTs,
  extendToRangeStart,
}: {
  account: DBAccount;
  positions: DBPosition[];
  rangeStartTs?: number;
  extendToRangeStart?: boolean;
}) {
  positions = positions?.toSorted((a, b) => {
    return (
      new Date(a.closeTransaction?.filledTimestamp ?? 0).getTime() -
      new Date(b.closeTransaction?.filledTimestamp ?? 0).getTime()
    );
  });
  const context = useRef<ContextType>({
    beginEquity: undefined,
    currentProfit: 0,
  }).current;

  const graphData = useCreateGraphFromPositions({
    positions,
    rangeStartTs,
    extendToRangeStart,
    minDistanceInMinutes: 5,
    initCallback: () => {
      context.currentProfit = START_PROFIT;
    },
    positionEntryCallback: (dataset, position, minTs) => {
      const { openTransaction, closeTransaction } = position;

      if (
        !["TAKE_PROFIT", "STOP_LOSS", "HEDGE_REPLACED"].includes(
          position.closeReason || "",
        )
      ) {
        return { minTs };
      }

      if (openTransaction?.filledTimestamp && openTransaction?.totalEquity) {
        if (!context.beginEquity) {
          context.beginEquity = openTransaction.totalEquity;
        }

        const timestamp = new Date(openTransaction.filledTimestamp).getTime();
        minTs = Math.min(minTs, timestamp);
      }

      if (closeTransaction?.filledTimestamp && closeTransaction?.totalEquity) {
        if (!context.beginEquity) {
          context.beginEquity = openTransaction.totalEquity;
        }

        const dollarPnL = getPositionPnLDollar(position);

        const timestamp = new Date(closeTransaction.filledTimestamp).getTime();

        context.currentProfit += dollarPnL ? dollarPnL : 0;

        minTs = Math.min(minTs, timestamp);
        dataset.push({
          x: timestamp,
          y: context.currentProfit,
        });
      }

      return { minTs };
    },
    lastEntryCallback: (dataset, minTs: number) => {
      dataset.push({
        x: minTs,
        y: START_PROFIT,
      });
      dataset.push({
        x: Date.now(),
        y: context.currentProfit,
      });
    },
  });

  return (
    <LineChart
      height={300}
      xAxis={[
        {
          dataKey: "x",
          valueFormatter: (
            value: number,
            context: AxisValueFormatterContext,
          ) => {
            const date = new Date(value);

            if (context.location === "tick") {
              return date.toLocaleDateString("nl-NL", {
                month: "short",
                day: "numeric",
              });
            }

            return date.toLocaleTimeString("nl-NL", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
          },
          min: graphData.minTs,
          max: graphData.maxTs,
          tickInterval: graphData.ticks,
        },
      ]}
      series={[
        {
          curve: "linear",
          dataKey: "y",
          label: "Profit over time (USD)",
        },
      ]}
      dataset={graphData.dataset}
    />
  );
}
